import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { useMetaAds } from '../../hooks/useMetaAds';
import { useContext } from 'react';
import { AuthContext } from '../../components/context/AuthContext';
import { Lock, Search as SearchIcon, Globe, ShoppingCart, MousePointer, Target, MessageSquare } from 'lucide-react';
import { ALL_CAMPAIGN_TYPES } from './campaignTypes';
import V0StyleLoader from '../../components/ui/V0StyleLoader';
import { useAds } from '../../components/context/AdsContext';


// Tarjeta genérica con el mismo estilo visual que PlatformCard
const GradientCard = ({ title, description, icon, onClick, notice, isSelected, disabled = false, blockedReason, footerIcon, footerIcons, badge, onUnlockClick, campaignKey }) => {
  const handleClick = () => {
    if (disabled) return;
    if (onClick) onClick();
  };
  const isBlocked = Boolean(disabled && blockedReason);
  const bottomIcons = footerIcons || (footerIcon ? [footerIcon] : []);
  const isSearchCampaign = campaignKey === 'GOOGLE_SEARCH_TRAFFIC';
  const isExpandCampaign = campaignKey === 'GOOGLE_PMAX_NO_CATALOG';
  const isShoppingCampaign = campaignKey === 'GOOGLE_PMAX_WITH_CATALOG';
  const isMetaTrafficCampaign = campaignKey === 'META_TRAFFIC';
  const isMetaConversionsCampaign = campaignKey === 'META_CONVERSIONS';
  const isMetaCatalogCampaign = campaignKey === 'META_CATALOG';
  return (
    <button
      type="button"
      aria-disabled={disabled}
      onClick={handleClick}
      className={`group relative w-full h-auto min-h-[120px] lg:min-h-[140px] text-center rounded-2xl p-3 lg:p-4 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 border-0 ${
        isBlocked 
          ? 'bg-gradient-to-b from-orange-50 to-orange-100 border-2 border-orange-300' 
          : 'bg-gradient-to-b from-fuchsia-50 to-blue-50'
      } ${
        isSelected ? 'ring-2 ring-primary/40' : ''
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {/* Candado pequeño arriba a la derecha cuando está bloqueado */}
      {isBlocked && (
        <div className="absolute top-3 right-3 pointer-events-none">
          <div className="w-8 h-8 rounded-full bg-white border-2 border-orange-300 shadow-md flex items-center justify-center">
            <Lock className="w-4 h-4 text-orange-600" />
          </div>
        </div>
      )}

      {(isSearchCampaign || isExpandCampaign || isShoppingCampaign || isMetaTrafficCampaign || isMetaConversionsCampaign || isMetaCatalogCampaign) ? (
        // Diseño especial para campañas con imagen
        <div className="flex items-center h-full">
          {/* Imagen - 1/3 del espacio */}
          <div className="flex-shrink-0 w-1/3 pr-4">
            <img 
              src={
                isSearchCampaign ? "/search-icon.png" : 
                isExpandCampaign ? "/expand-icon.png" : 
                isShoppingCampaign ? "/shopping-icon.png" :
                isMetaTrafficCampaign ? "/meta-traffic-image.jpg" :
                isMetaConversionsCampaign ? "/meta-conversions-image.jpg" :
                isMetaCatalogCampaign ? "/meta-catalog-image.jpg" :
                "/shopping-icon.png"
              }
              alt={
                isSearchCampaign ? "Search Icon" : 
                isExpandCampaign ? "Expand Icon" : 
                isShoppingCampaign ? "Shopping Icon" :
                isMetaTrafficCampaign ? "Meta Traffic Image" :
                isMetaConversionsCampaign ? "Meta Conversions Image" :
                isMetaCatalogCampaign ? "Meta Catalog Image" :
                "Shopping Icon"
              }
              className="w-[65%] h-auto rounded-xl"
            />
          </div>
          
          {/* Contenido de texto - 2/3 del espacio */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="text-gray-900 font-semibold tracking-tight text-left mb-2">{title}</div>
            {description && (
              <div className="text-sm text-gray-600 leading-snug text-left">{description}</div>
            )}
          </div>
        </div>
      ) : (
        // Diseño original para otras campañas
        <div className="flex flex-col items-center">
          {icon && (
            <div className="bg-opacity-0 mb-2 inline-flex h-10 w-10 items-center justify-center rounded-2xl">
              {icon}
            </div>
          )}
          {badge && (
            <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {badge}
            </div>
          )}
          <div className="text-gray-900 font-semibold tracking-tight">{title}</div>
          {description && (
            <div className="text-sm text-gray-600 mt-0.5 leading-snug max-w-[28ch] mx-auto">{description}</div>
          )}
          {bottomIcons.length > 0 && (
            <div className="absolute bg-opacity-0 bottom-3 left-1/2 -translate-x-1/2 flex justify-center gap-2 text-gray-500 pointer-events-none">
              {bottomIcons.map((ic, idx) => (
                <div key={idx} className="w-6 h-6 bg-opacity-0 rounded-full flex items-center justify-center">
                  {ic}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Motivo de bloqueo clickeable en la parte inferior */}
      {isBlocked && onUnlockClick && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-auto">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onUnlockClick();
            }}
            className="px-2 py-1 text-[10px] font-medium text-orange-700 bg-orange-200 hover:bg-orange-300 border border-orange-300 rounded-full transition-colors duration-200 shadow-sm"
          >
            {blockedReason}
          </button>
        </div>
      )}

      {/* Notice normal cuando no está bloqueado */}
      {!isBlocked && notice && (
        <div className="mt-3 text-left">
          <div className="flex items-start gap-2 text-xs text-gray-700">
            <span className="flex-1">{notice.text}</span>
            {notice.onClick && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); notice.onClick && notice.onClick(); }}
                className="text-primary underline whitespace-nowrap"
              >
                {notice.ctaLabel || 'Configurar ahora'}
              </button>
            )}
          </div>
        </div>
      )}
    </button>
  );
};

/**
 * Step 2: Campaign Type Selection Component
 * 
 * This component renders the campaign type selection step of the campaign wizard.
 * Users can choose from different campaign types based on the selected platform.
 * The component handles platform-specific requirements and shows appropriate warnings.
 * 
 * @param {Object} props - Component props
 * @param {string} props.platform - Selected platform ('google', 'meta', 'whatsapp', 'all')
 * @param {string} props.selectedCampaignType - Currently selected campaign type
 * @param {Function} props.onCampaignTypeSelect - Callback function when a campaign type is selected
 * @param {boolean} props.isCreatingCampaign - Whether a campaign is being created
 * @param {string} props.campaignError - Error message if campaign creation failed
 * @returns {JSX.Element} Campaign type selection interface
 */
const Step2CampaignType = ({ platform, selectedCampaignType, onCampaignTypeSelect, isCreatingCampaign, campaignError }) => {
  const navigate = useNavigate();
  const { auth, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const { activeGoogleAccount } = useAds();

  const hasWhatsappConnected = Boolean(auth.user?.meta_whatsapp_business_account_phone_number);

  // Generate campaign options based on platform and requirements
  const campaignOptions = React.useMemo(() => {
    const gConvOk = Boolean(activeGoogleAccount?.has_enabled_and_valid_conversions);

    const getCampaignTypeDisabled = (campaignType) => {
      // Use special_platform if it exists, otherwise use the original platform
      const effectivePlatform = campaignType.special_platform || campaignType.platform;
      if (effectivePlatform === 'google') {
        if (!hasGoogleAdsConnected) return true;
        if (campaignType.requires_conversions && !gConvOk) return true;

      } else if (effectivePlatform === 'meta' || effectivePlatform === 'whatsapp') {
        if (!hasMetaConnected) return true;
        if (campaignType.requires_whatsapp_business && !hasWhatsappConnected) return true;
      }
      return false; 
    }

    const optionsByPlatform = {};
    const platforms = ['google', 'meta', 'whatsapp'];
    
    platforms.forEach(platform => {
      optionsByPlatform[platform] = Object.values(ALL_CAMPAIGN_TYPES)
        .filter((type) => {
          // If campaign has special_platform, use that instead of the original platform
          const effectivePlatform = type.special_platform || type.platform;
          return effectivePlatform === platform;
        })
        .map((type) => ({
          key: type.key,
          title: type.title,
          description: type.description,
          disabled: getCampaignTypeDisabled(type),
          platform: type.platform,
          special_platform: type.special_platform,
          campaign_type: type.campaign_type,
          chatbotMessage: type.chatbotMessage,
        }));
    });

    if (platform === 'all' || !platform) {
      return [
        ...optionsByPlatform.google,
        ...optionsByPlatform.meta,
        ...optionsByPlatform.whatsapp,
      ];
    }

    return optionsByPlatform[platform] || [];
  }, [platform, hasGoogleAdsConnected, hasMetaConnected, activeGoogleAccount, hasWhatsappConnected]);

  // Check if platform is locked (no connection)
  const platformLocked = (platform === 'google' && !hasGoogleAdsConnected)
    || (platform === 'meta' && !hasMetaConnected)
    || (platform === 'whatsapp' && !hasMetaConnected); // Solo cuando no está conectado a Meta

  const googleLocked = !hasGoogleAdsConnected;
  const metaLocked = !hasMetaConnected;
  const whatsappNeedsMetaConnection = !hasMetaConnected; // Solo cuando no está conectado a Meta

  const showFooterCTA = (platformLocked || (platform === 'all' && (googleLocked || metaLocked || whatsappNeedsMetaConnection)));

  const hasGoogleConversions = Boolean(activeGoogleAccount?.has_enabled_and_valid_conversions);

  const getBlockedReason = (optKey) => {
    // Find the campaign type to check if it has special_platform
    const campaignType = Object.values(ALL_CAMPAIGN_TYPES).find(type => 
      type.key === optKey
    );
    
    if (!campaignType) return null;
    
    const effectivePlatform = campaignType.special_platform || campaignType.platform;
    
    // Caso especial para WhatsApp
    if (effectivePlatform === 'whatsapp') {
      // Si no está conectado a Meta, mostrar "Conectá cuenta"
      if (!hasMetaConnected) return 'Conectá cuenta';
      // Si está conectado a Meta pero no tiene WhatsApp Business, mostrar el mensaje específico
      if (!hasWhatsappConnected) return 'Falta Whatsapp Business';
    }
    
    // Si la plataforma está bloqueada (no conectada)
    if (effectivePlatform === 'google' && !hasGoogleAdsConnected) {
      return 'Conectá cuenta';
    }
    if ((effectivePlatform === 'meta' || effectivePlatform === 'whatsapp') && !hasMetaConnected) {
      return 'Conectá cuenta';
    }
    
    // Use the campaign_type directly from the campaign type object
    const campaignTypeKey = campaignType.campaign_type;
    
    switch (campaignTypeKey) {
      case 'SEARCH':
        // Search campaigns require conversions
        return hasGoogleConversions ? null : 'Falta Conversión';
        
      case 'PERFORMANCE_MAX':
        return hasGoogleConversions ? null : 'Falta Conversión';
        
      case 'OUTCOME_TRAFFIC':
        // Traffic campaigns don't require pixel
        return null;
        
      case 'OUTCOME_SALES':
        return null;
        
      case 'WHATSAPP_BUSINESS':
        return hasWhatsappConnected ? null : 'Falta Whatsapp Business';
        
      default:
        return null;
    }
  };

  const getUnlockLink = (optKey, blockedReason) => {
    // Find the campaign type to check if it has special_platform
    const campaignType = Object.values(ALL_CAMPAIGN_TYPES).find(type => 
      type.key === optKey
    );
    
    if (!campaignType) return null;
    
    const effectivePlatform = campaignType.special_platform || campaignType.platform;
    
    // Enlaces específicos según el tipo de bloqueo
    switch (blockedReason) {
      case 'Conectá cuenta':
        // Si necesita conectar cuenta, ir a connections
        return '/profile/brands';
        
      case 'Falta Conversión':
        // Si falta conversiones (Google) o pixel (Meta)
        if (effectivePlatform === 'google') {
          return 'https://support.google.com/google-ads/answer/12216226?hl=es-419';
        }
        if (effectivePlatform === 'meta') {
          return 'https://www.facebook.com/business/help/952192354843755?id=1205376682832142';
        }
        return '/profile/brands';
        
      case 'Falta Catálogo':
        // Si falta Merchant Center (Google)
        if (effectivePlatform === 'google') {
          return 'https://support.google.com/merchants/answer/6159060';
        }
        // Si falta catálogo de Meta
        if (effectivePlatform === 'meta') {
          return 'http://facebook.com/business/help/169396597334438';
        }
        return '/profile/brands';
        
      case 'Falta Whatsapp Business':
        return 'https://business.whatsapp.com/products/business-app-get-started?lang=es_LA';
        
      default:
        return '/profile/brands';
    }
  };

  return (
    <div className="space-y-4">
      <div className="max-w-5xl mx-auto">
        {!isCreatingCampaign && (
          <h3 className="text-sm font-semibold text-white mb-2">Seleccioná el tipo de campaña</h3>
        )}
        
        {/* Error message */}
        {campaignError && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
            <p className="text-sm text-red-300">{campaignError}</p>
          </div>
        )}
        
        {/* Loading overlay */}
        {isCreatingCampaign && (
          <div className="fixed inset-0 modal-safe-area z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <V0StyleLoader />
          </div>
        )}
        
        {!isCreatingCampaign && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-5">
          {campaignOptions.map((opt) => {
            const warn = opt.warn;
            const disabled = Boolean(opt.disabled) || platformLocked || isCreatingCampaign;
            const blockedReason = disabled ? getBlockedReason(opt.key) : null;
            const unlockLink = blockedReason ? getUnlockLink(opt.key, blockedReason) : null;
            
            // Icons for each card
            const campaignTypeKey = opt.key.split(':')[0];
            const iconNode = (
              campaignTypeKey === 'SEARCH'
            ) ? <SearchIcon className="w-5 h-5 text-gray-600" />
              : campaignTypeKey === 'PERFORMANCE_MAX' ? (
                // Check if it's catalog version
                Object.values(ALL_CAMPAIGN_TYPES).find(type => 
                  type.key === opt.key
                )?.requires_merchant_center
                  ? <ShoppingCart className="w-5 h-5 text-gray-600" />
                  : <Globe className="w-5 h-5 text-gray-600" />
              )
              : campaignTypeKey === 'OUTCOME_TRAFFIC' ? <MousePointer className="w-5 h-5 text-gray-600" />
              : campaignTypeKey === 'OUTCOME_SALES' ? (
                // Check if it's catalog version
                Object.values(ALL_CAMPAIGN_TYPES).find(type => 
                  type.key === opt.key
                )?.requires_catalog
                  ? <ShoppingCart className="w-5 h-5 text-gray-600" />
                  : <Target className="w-5 h-5 text-gray-600" />
              )
              : campaignTypeKey === 'WHATSAPP_BUSINESS' ? <MessageSquare className="w-5 h-5 text-gray-600" />
              : null;
              
            return (
              <GradientCard
                key={opt.key}
                title={opt.title}
                description={opt.description}
                icon={iconNode}
                notice={platformLocked ? null : warn}
                isSelected={selectedCampaignType?.key === opt.key}
                disabled={disabled}
                blockedReason={blockedReason}
                footerIcon={null}
                campaignKey={opt.key}
                onUnlockClick={unlockLink ? () => {
                  if (unlockLink.startsWith('http')) {
                    // Enlace externo - abrir en nueva pestaña
                    window.open(unlockLink, '_blank');
                  } else {
                    // Enlace interno - navegar
                    navigate(unlockLink);
                  }
                } : null}
                onClick={() => {
                  if (disabled) return;
                  onCampaignTypeSelect(opt);
                }}
              />
            );
          })}
          </div>
        )}
      </div>

      {/* Fixed footer when platform is locked (no connection) */}
      {showFooterCTA && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur border-t border-white/20 shadow-sm transition-all duration-500 ease-in-out z-40" style={{ paddingLeft: 'var(--sidebar-width, 0px)' }}>
          <div className="max-w-5xl mx-auto px-4 py-3 lg:py-4 md:py-5 flex items-center justify-center">
            <button
              type="button"
              onClick={() => {
                if ((platform === 'google') || (platform === 'all' && googleLocked && !metaLocked && !whatsappNeedsMetaConnection)) {
                  connectGoogleAds(false);
                } else if ((platform === 'meta') || (platform === 'whatsapp') || (platform === 'all' && (metaLocked || whatsappNeedsMetaConnection))) {
                  startMetaOAuth(false);
                } else {
                  navigate('/profile/brands');
                }
              }}
              className="px-4 lg:px-5 md:px-6 py-2.5 lg:py-3 md:py-3.5 rounded-xl bg-primary text-white text-sm md:text-base hover:bg-primary flex items-center gap-2"
            >
              <Lock className="w-4 h-4" /> Conectá tu cuenta para desbloquear las campañas
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2CampaignType;
